import { ref } from "vue";

/**
 * Unsent text kept per conversation, as WhatsApp Web keeps a draft: in this browser, for the
 * person signed in, until it is sent or erased. Leaving the chat, choosing another or pressing F5
 * keeps it; the list shows it as "Rascunho: …" until the chat is opened again.
 */
const PREFIX = "donetalk.drafts.";
const MAX_CHARS = 4096;
/** Drafts nobody came back to are dropped after a month. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface Stored {
  text: string;
  at: number;
}

/** chatId → text, for the person signed in. */
export const drafts = ref<Record<string, string>>({});
let owner = "";
let stored: Record<string, Stored> = {};

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function persist() {
  try {
    storage()?.setItem(PREFIX + owner, JSON.stringify(stored));
  } catch {
    // Full or blocked storage: the draft lives until the page closes.
  }
}

/** Reads the drafts of who signed in on this browser (another person's are never shown). */
export function loadDrafts(userId: string, now = Date.now()): void {
  if (owner === userId) return;
  owner = userId;
  stored = {};
  try {
    const raw = JSON.parse(storage()?.getItem(PREFIX + userId) ?? "{}");
    for (const [id, d] of Object.entries(raw as Record<string, Stored>))
      if (typeof d?.text === "string" && now - Number(d.at) < MAX_AGE_MS)
        stored[id] = { text: d.text, at: Number(d.at) };
  } catch {
    stored = {};
  }
  drafts.value = Object.fromEntries(
    Object.entries(stored).map(([id, d]) => [id, d.text]),
  );
}

export function draftOf(chatId: string): string {
  return owner ? (drafts.value[chatId] ?? "") : "";
}

/** Keeps the text as the chat's draft; blank text erases it. */
export function setDraft(chatId: string, text: string, now = Date.now()): void {
  if (!owner || !chatId) return;
  if (!text.trim()) {
    if (!(chatId in stored)) return;
    delete stored[chatId];
    const rest = { ...drafts.value };
    delete rest[chatId];
    drafts.value = rest;
  } else {
    const value = text.slice(0, MAX_CHARS);
    if (stored[chatId]?.text === value) return;
    stored[chatId] = { text: value, at: now };
    drafts.value = { ...drafts.value, [chatId]: value };
  }
  persist();
}

/** Signing out erases the drafts kept in this browser, as WhatsApp Web does. */
export function forgetDrafts(): void {
  try {
    if (owner) storage()?.removeItem(PREFIX + owner);
  } catch {
    // Blocked storage: nothing was kept.
  }
  owner = "";
  stored = {};
  drafts.value = {};
}
